import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import type { SearchParams, SortParams } from "../components/Manager/types";
import { useEffect, useCallback } from "react";

export type BannerType = "restaurant" | "grocery_store" | "liquor_store";

export type Banner = {
  id: number;
  name: string;
  type: BannerType;
  created_at: string;
  updated_at: string;
};

export type FetchResult<T> = {
  data: T[];
  hasMore: boolean;
  totalCount?: number;
};

/**
 * Search banners by name prefix. Returns empty array for empty search string.
 */
export async function searchBanners(
  searchText: string,
  limit = 10,
): Promise<Banner[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("banners")
    .select("*")
    .ilike("name", `%${searchText}%`)
    .order("name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return (data ?? []) as Banner[];
}

/**
 * Fetch a single banner by ID.
 */
export async function fetchBannerById(id: number): Promise<Banner | null> {
  const { data, error } = await supabase
    .from("banners")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data as Banner;
}

export async function fetchBanners(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<Banner>> {
  // First, get total count with search filters
  let countQuery = supabase
    .from("banners")
    .select("*", { count: "exact", head: true });

  // Apply text search filter for count (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    countQuery = countQuery.ilike("name", searchText);
  }

  const { count } = await countQuery;

  // Now get the data
  let query = supabase.from("banners").select("*");

  // Apply sorting (default to name ascending if not specified)
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";
  query = query.order(sortColumn, { ascending: sortAscending });

  // Apply text search filter (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    query = query.ilike("name", searchText);
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  const items = (data ?? []) as Banner[];
  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

const bannersAtom = atom<Banner[]>([]);

export function useBanners() {
  const [banners, setBanners] = useAtom(bannersAtom);

  const fetchAllBanners = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("banners")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setBanners((data ?? []) as Banner[]);
    } catch (err) {
      console.error("Error fetching banners:", err);
    }
  }, [setBanners]);

  useEffect(() => {
    fetchAllBanners();
  }, [fetchAllBanners]);

  return [banners, fetchAllBanners] as const;
}
